import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Search, Eye, Download, Printer, Car, Users, FileText, BarChart3, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const ConsultasReportes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/consultas-reportes/consultas" replace />} />
      <Route path="/consultas" element={<ConsultasDinamicas />} />
      <Route path="/reportes" element={<GeneracionReportes />} />
      <Route path="/runt" element={<ConsultaRUNT />} />
      <Route path="/simit" element={<ConsultaSIMIT />} />
    </Routes>
  );
};

const ConsultasDinamicas: React.FC = () => {
  const [tipoConsulta, setTipoConsulta] = useState('');
  const [valorConsulta, setValorConsulta] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);

  // Datos de prueba
  const datosPrueba = [
    {
      id: '1',
      tipo: 'Vehículo',
      identificador: 'ABC123',
      propietarioConductor: 'Juan Pérez',
      estado: 'Activo',
      detalle: 'Chevrolet Spark 2020',
      tipoColor: 'purple'
    },
    {
      id: '2',
      tipo: 'Licencia',
      identificador: '1234567890',
      propietarioConductor: 'María Gómez',
      estado: 'Vigente',
      detalle: 'Categoría B1',
      tipoColor: 'green'
    },
    {
      id: '3',
      tipo: 'Tarjeta',
      identificador: 'TO-987654',
      propietarioConductor: 'Empresa XYZ',
      estado: 'Vigente',
      detalle: 'Ruta 45 - Bogotá',
      tipoColor: 'orange'
    }
  ];

  const tiposConsulta = [
    { value: 'vehiculo', label: 'Vehículo' },
    { value: 'licencia', label: 'Licencia de Conducción' },
    { value: 'tarjeta', label: 'Tarjeta de Operación' },
    { value: 'propietario', label: 'Propietario' },
    { value: 'placa', label: 'Placa' }
  ];

  const getTipoBadge = (tipo: string, color: string) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-800',
      green: 'bg-green-100 text-green-800',
      orange: 'bg-orange-100 text-orange-800',
      blue: 'bg-blue-100 text-blue-800'
    };
    return colors[color as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoBadge = (estado: string) => {
    if (estado === 'Activo' || estado === 'Vigente') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-blue-100 text-blue-800';
  };

  const handleConsultar = () => {
    if (!tipoConsulta || !valorConsulta) {
      toast.error('Por favor selecciona un tipo de consulta e ingresa un valor');
      return;
    }
    
    setResultados(datosPrueba);
    toast.success('Consulta realizada exitosamente');
  };

  const handleVerDetalle = (id: string) => {
    toast.success(`Viendo detalles del registro ${id}`);
  };

  const handleExportar = (id: string) => {
    toast.success(`Exportando registro ${id}`);
  };

  const handleImprimir = (id: string) => {
    toast.success(`Imprimiendo registro ${id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Consultas Dinámicas</h1>
        <p className="text-gray-600">Accede a información consolidada y actualizada sobre parque automotor, licencias y tarjetas de operación</p>
      </div>

      {/* Search Form */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Search className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consultas Dinámicas</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Tipo de Consulta</label>
            <select
              value={tipoConsulta}
              onChange={(e) => setTipoConsulta(e.target.value)}
              className="input"
            >
              <option value="">Seleccione</option>
              {tiposConsulta.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="label">Valor a Consultar</label>
            <input
              type="text"
              placeholder="Ingrese el dato a consultar"
              value={valorConsulta}
              onChange={(e) => setValorConsulta(e.target.value)}
              className="input"
            />
          </div>
          
          <div className="flex items-end">
            <button onClick={handleConsultar} className="btn btn-primary w-full">
              <Search className="w-4 h-4 mr-2" />
              Consultar
            </button>
          </div>
        </div>
      </div>

      {/* Information Types */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Eye className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Visualización de Información</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Parque automotor</h4>
            <p className="text-sm text-gray-600">Datos técnicos y jurídicos, estado, historial de trámites, restricciones.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Licencias de conducción</h4>
            <p className="text-sm text-gray-600">Categoría, fechas, infracciones, historial.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Tarjetas de operación</h4>
            <p className="text-sm text-gray-600">Vigencia, rutas, empresa asociada.</p>
          </div>
        </div>
      </div>

      {/* Results Table */}
      {resultados.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Tipo</th>
                  <th className="table-header-cell">Identificador</th>
                  <th className="table-header-cell">Propietario/Conductor</th>
                  <th className="table-header-cell">Estado</th>
                  <th className="table-header-cell">Detalle</th>
                  <th className="table-header-cell">Acciones</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {resultados.map((resultado) => (
                  <tr key={resultado.id} className="table-row">
                    <td className="table-cell">
                      <span className={`badge ${getTipoBadge(resultado.tipo, resultado.tipoColor)}`}>
                        {resultado.tipo}
                      </span>
                    </td>
                    <td className="table-cell font-medium">{resultado.identificador}</td>
                    <td className="table-cell">{resultado.propietarioConductor}</td>
                    <td className="table-cell">
                      <span className={`badge ${getEstadoBadge(resultado.estado)}`}>
                        {resultado.estado}
                      </span>
                    </td>
                    <td className="table-cell">{resultado.detalle}</td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleVerDetalle(resultado.id)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded-full"
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExportar(resultado.id)}
                          className="p-1 text-green-600 hover:bg-green-100 rounded-full"
                          title="Exportar"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleImprimir(resultado.id)}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded-full"
                          title="Imprimir"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const GeneracionReportes: React.FC = () => {
  const [tipoReporte, setTipoReporte] = useState('');
  const [fecha, setFecha] = useState('');
  const [ciudad, setCiudad] = useState('');

  const tiposReporte = [
    { value: 'vehiculos', label: 'Reporte de Vehículos' },
    { value: 'licencias', label: 'Reporte de Licencias' },
    { value: 'infracciones', label: 'Reporte de Infracciones' },
    { value: 'tramites', label: 'Reporte de Trámites' },
    { value: 'estadisticas', label: 'Estadísticas Generales' }
  ];

  const handleGenerarReporte = () => {
    if (!tipoReporte) {
      toast.error('Por favor selecciona un tipo de reporte');
      return;
    }
    toast.success('Generando reporte...');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Generación de Reportes</h1>
        <p className="text-gray-600">Genera reportes personalizados con diferentes criterios y formatos</p>
      </div>

      {/* Report Form */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Generación de Reportes</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label">Tipo de Reporte</label>
            <select
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value)}
              className="input"
            >
              <option value="">Seleccione</option>
              {tiposReporte.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="label">Fecha</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="input"
            />
          </div>
          
          <div>
            <label className="label">Ciudad</label>
            <input
              type="text"
              placeholder="Ciudad"
              value={ciudad}
              onChange={(e) => setCiudad(e.target.value)}
              className="input"
            />
          </div>
          
          <div className="flex items-end">
            <button onClick={handleGenerarReporte} className="btn btn-primary w-full">
              <BarChart3 className="w-4 h-4 mr-2" />
              Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {/* Report Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Car className="w-8 h-8 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">Vehículos</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">Reportes de matrículas, traspasos y modificaciones</p>
          <button className="btn btn-primary text-sm">Generar</button>
        </div>

        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="w-8 h-8 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900">Licencias</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">Reportes de licencias de conducción y renovaciones</p>
          <button className="btn btn-primary text-sm">Generar</button>
        </div>

        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-yellow-500" />
            <h3 className="text-lg font-semibold text-gray-900">Infracciones</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">Reportes de infracciones y multas</p>
          <button className="btn btn-primary text-sm">Generar</button>
        </div>
      </div>
    </div>
  );
};

const ConsultaRUNT: React.FC = () => {
  const [placa, setPlaca] = useState('');
  const [resultado, setResultado] = useState<any>(null);

  const handleConsultarRUNT = () => {
    if (!placa) {
      toast.error('Por favor ingresa una placa');
      return;
    }
    
    // Simular consulta a RUNT
    setResultado({
      placa: placa,
      marca: 'Chevrolet',
      modelo: 'Spark',
      año: 2020,
      color: 'Blanco',
      propietario: 'Juan Pérez',
      estado: 'Activo',
      restricciones: []
    });
    
    toast.success('Consulta a RUNT realizada exitosamente');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Consulta RUNT</h1>
        <p className="text-gray-600">Consulta información vehicular en el Registro Único Nacional de Tránsito</p>
      </div>

      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consulta RUNT</h3>
        </div>
        
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="label">Placa del Vehículo</label>
            <input
              type="text"
              placeholder="Ej: ABC123"
              value={placa}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button onClick={handleConsultarRUNT} className="btn btn-primary">
              <Search className="w-4 h-4 mr-2" />
              Consultar RUNT
            </button>
          </div>
        </div>
      </div>

      {resultado && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resultado de la Consulta</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Placa</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.placa}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Marca</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.marca}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Modelo</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.modelo}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Año</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.año}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Color</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.color}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Propietario</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.propietario}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Estado</label>
              <span className="badge badge-success">{resultado.estado}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ConsultaSIMIT: React.FC = () => {
  const [documento, setDocumento] = useState('');
  const [resultado, setResultado] = useState<any>(null);

  const handleConsultarSIMIT = () => {
    if (!documento) {
      toast.error('Por favor ingresa un documento');
      return;
    }
    
    // Simular consulta a SIMIT
    setResultado({
      documento: documento,
      nombre: 'María Gómez',
      infracciones: [
        { fecha: '2024-01-10', tipo: 'Exceso de velocidad', monto: 500000 },
        { fecha: '2024-01-05', tipo: 'Pico y placa', monto: 300000 }
      ],
      totalMultas: 800000,
      puntos: 15
    });
    
    toast.success('Consulta a SIMIT realizada exitosamente');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Consulta SIMIT</h1>
        <p className="text-gray-600">Consulta información de multas e infracciones en el Sistema de Información de Multas e Infracciones de Tránsito</p>
      </div>

      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consulta SIMIT</h3>
        </div>
        
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="label">Documento de Identidad</label>
            <input
              type="text"
              placeholder="Ej: 12345678"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              className="input"
            />
          </div>
          <div className="flex items-end">
            <button onClick={handleConsultarSIMIT} className="btn btn-primary">
              <Search className="w-4 h-4 mr-2" />
              Consultar SIMIT
            </button>
          </div>
        </div>
      </div>

      {resultado && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resultado de la Consulta</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-500">Documento</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.documento}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Nombre</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.nombre}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Puntos</label>
              <p className="text-lg font-semibold text-gray-900">{resultado.puntos}</p>
            </div>
          </div>
          
          <div className="mb-4">
            <h4 className="text-md font-semibold text-gray-900 mb-2">Infracciones</h4>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Fecha</th>
                    <th className="table-header-cell">Tipo</th>
                    <th className="table-header-cell">Monto</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {resultado.infracciones.map((infraccion: any, index: number) => (
                    <tr key={index} className="table-row">
                      <td className="table-cell">{infraccion.fecha}</td>
                      <td className="table-cell">{infraccion.tipo}</td>
                      <td className="table-cell">${infraccion.monto.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-lg font-semibold text-red-800">
              Total Multas: ${resultado.totalMultas.toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultasReportes;
